
const {
  formatNumber,
  filterObjectKeys,
  date,
  loggerBase,
  statDayNumber,
} = require('../utils')

const {
  ONE_YEARISH,
  ONE_DAY,
  ONE_HOUR,
} = require('../timeConstants')

const {
  abTest,
} = require('../testingUtils')

const { TIKTOK_NATIVE, INSTAGRAM_NATIVE, YOUTUBE_NATIVE } = require('../multicastingConstants')


// Update this and getUniqueSubmittableString() when we add a new submittable type
exports.CAMPAIGN_SUBMITTABLE_TYPES = {
  TIKTOK_POST: 'tiktokPost',
  INSTAGRAM_POST: 'instagramPost',
  YOUTUBE_POST: 'youtubePost',
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
}

// This is our main point of truth for defaulting submissionType for backwards compatibility
exports.getSubmissionTypeFromCampaign = function(campaignData) {
  const { submissionType = exports.CAMPAIGN_SUBMITTABLE_TYPES.TIKTOK_POST } = campaignData
  return submissionType
}

exports.getSubmissionTypeFromRelationship = function(relationshipData) {
  const { brandCampaignRelationshipData = {} } = relationshipData
  const { submissionType = exports.CAMPAIGN_SUBMITTABLE_TYPES.TIKTOK_POST } = brandCampaignRelationshipData
  return submissionType
}

exports.getUniqueSubmittableKeyName = function(submittableType) {
  switch (submittableType) {
    case exports.CAMPAIGN_SUBMITTABLE_TYPES.TIKTOK_POST:
      return 'tiktokPostId'

    case exports.CAMPAIGN_SUBMITTABLE_TYPES.INSTAGRAM_POST:
      return 'instagramPostId'

    case exports.CAMPAIGN_SUBMITTABLE_TYPES.YOUTUBE_POST:
      return 'youtubePostId'

    case exports.CAMPAIGN_SUBMITTABLE_TYPES.TEXT:
      return 'text'

    case exports.CAMPAIGN_SUBMITTABLE_TYPES.IMAGE:
      return 'imageUrl'

    case exports.CAMPAIGN_SUBMITTABLE_TYPES.VIDEO:
      return 'vodUrl'

    default:
      return undefined
  }
}

exports.getUniqueSubmittableString = function(submittableType, submittable) {
  const uniqueSubmittableField = exports.getUniqueSubmittableKeyName(submittableType)
  if (!uniqueSubmittableField) return undefined

  return submittable[uniqueSubmittableField]
}

exports.getUniqueSubmissionString = function(relationshipData) {
  const { submission = {} } = relationshipData
  const submissionType = exports.getSubmissionTypeFromRelationship(relationshipData)
  return exports.getUniqueSubmittableString(submissionType, submission)
}

exports.getSubmissionStatus = function(relationshipData) {
  return exports.getUniqueSubmissionString(relationshipData) !== undefined
}

exports.getUniqueDraftString = function(relationshipData) {
  const { draft = {}, brandCampaignRelationshipData = {} } = relationshipData
  const { draftType = exports.CAMPAIGN_SUBMITTABLE_TYPES.VIDEO } = brandCampaignRelationshipData // Defaulting this for backwards compatability
  return exports.getUniqueSubmittableString(draftType, draft)
}

exports.getDraftStatus = function(relationshipData) {
  return exports.getUniqueDraftString(relationshipData) !== undefined
}

exports.getExternalPlatformBySubmittableType = function(submittableType) {
  if (submittableType === exports.CAMPAIGN_SUBMITTABLE_TYPES.TIKTOK_POST) return TIKTOK_NATIVE
  if (submittableType === exports.CAMPAIGN_SUBMITTABLE_TYPES.INSTAGRAM_POST) return INSTAGRAM_NATIVE
  if (submittableType === exports.CAMPAIGN_SUBMITTABLE_TYPES.YOUTUBE_POST) return YOUTUBE_NATIVE
  return undefined
}

exports.getPolynomialCpmFromInputs = (inputs, averageViewCount) => {
  const { anchor1 = {}, anchor2 = {} } = inputs
  const { averageViewCount: vc1, maxEarnings: emax1 } = anchor1
  const { averageViewCount: vc2, maxEarnings: emax2 } = anchor2

  if (!vc1 || !vc2 || !emax1 || !emax2) {
    return 0
  }

  if (vc1 === vc2) {
    return 0
  }

  if ((vc1 > vc2) !== (emax1 > emax2)) {
    return 0
  }

  if ((emax1 / vc1) === (emax2 / vc2)) { // constant CPM
    return emax1 / vc1 * 1000
  }

  // get constants for the formula emax = A * vc ^ B, where emax = max earnings and vc = view count
  const B = Math.log(emax1 / emax2) / Math.log(vc1 / vc2)
  const A = emax1 / Math.pow(vc1, B)

  const emax = A * Math.pow(averageViewCount, B)
  return emax / averageViewCount * 1000
}

// round to whole amounts, and never offer less than $1
exports.formatOfferEarningsNicelyAsString = (rawValue) => {
  return formatNumber(Math.max(1, Math.round(rawValue)), 0)
}
exports.formatOfferEarningsNicelyAsNumber = (rawValue) => {
  return Number(exports.formatOfferEarningsNicelyAsString(rawValue).replace(/[^\d.]/g, '')) // the `replace` filters out commas
}

// we want to only show offers for view counts with 1 sig fig (for example, "500K views" instead of "523K views")
// if sigFigCount is not an integer, then it will round to the next ".5"
exports.roundLog10NumberToSigFig = (log10Number, sigFigCount = 1) => {
  const integerSigFigCount = Math.floor(sigFigCount)
  const includeFives = sigFigCount > integerSigFigCount
  const sigFig = Math.pow(10, Math.floor(log10Number) + 1 - integerSigFigCount) * (includeFives ? 0.5 : 1)
  return Math.round(Math.pow(10, log10Number) / sigFig) * sigFig
}
exports.roundNumberToSigFig = (number, sigFigCount = 1) => {
  return exports.roundLog10NumberToSigFig(Math.log10(number), sigFigCount)
}

// legacy
exports.roundLog10NumberToOneSigFig = (log10Number, additionalSigFigs = 0) => {
  return exports.roundLog10NumberToSigFig(log10Number, additionalSigFigs + 1)
}
exports.roundNumberToOneSigFig = (number, additionalSigFigs = 0) => {
  return exports.roundLog10NumberToOneSigFig(Math.log10(number), additionalSigFigs)
}

exports.getBrandCampaignUserCohort = (relationshipData, isOffPlatformRelationship) => {
  const {
    draft = {},
    submissionData = {},
    submissionApproved,
    submissionCertified,
    fraudAlertClearDate,
    offerAccepted,
    brandStaged,
    brandStagedSilent,
    brandApproved,
    draftRequested,
    draftBrandReview,
    brandDeferred,
  } = relationshipData

  const { fraudWarningDetectionDates = {} } = submissionData
  const submissionFraudAlert = Object.keys(fraudWarningDetectionDates)
    .some(key => fraudWarningDetectionDates[key] && (!fraudAlertClearDate || date(fraudWarningDetectionDates[key]) > date(fraudAlertClearDate)))

  if (exports.getSubmissionStatus(relationshipData)) {
    if (submissionApproved && submissionCertified) return 'submitted-approved-certified'
    if (submissionFraudAlert) return 'submitted-fraud-alert'
    if (submissionApproved || isOffPlatformRelationship) return 'submitted-approved'
    return 'submitted-pending'
  }

  switch (isOffPlatformRelationship ? true : offerAccepted) {
    case true:
      switch (brandApproved) {
        case true: return 'applied-approved'
        case false: return 'applied-rejected'
        default:
          if (brandStaged) return 'applied-staged'
          if (brandStagedSilent) return 'applied-staged-silent'
          else if (exports.getDraftStatus(relationshipData) && draftBrandReview) return 'applied-draft-brand-review'
          else if (exports.getDraftStatus(relationshipData)) return 'applied-draft'
          else if (draftRequested) return 'applied-drafting'
          else if (brandDeferred) return 'applied-deferred'
          return 'applied-pending'
      }
    case false:
      return 'declined'
    default:
      break
  }

  return 'offered'
}

// NOTE WHEN UPDATING: we have several places in our backend that depends on this code to function
// - Brand campaign to user relationship checking
// - Active trend to user relationship checking
// - User messaging targeting
exports.getRelationshipMatchesTarget = function(relationshipData, target, connectedAccountList = []) {
  const {
    userRelationshipData = {},
  } = relationshipData

  const { log10MetricsByPlatform = {} } = userRelationshipData
  const submissionType = exports.getSubmissionTypeFromRelationship(relationshipData)
  const externalPlatform = exports.getExternalPlatformBySubmittableType(submissionType)

  const selectedLog10Metrics = log10MetricsByPlatform[externalPlatform] || {}

  if (target.priorityOfferGroupId) {
    const {
      priorityOfferGroupIdList = [],
    } = relationshipData
    if (!priorityOfferGroupIdList.includes(target.priorityOfferGroupId)) {
      return false
    }
  }

  if (target.customUserIdList) {
    const {
      userId,
    } = relationshipData
    if (!userId) return false

    if (!target.customUserIdList.includes(userId)) {
      return false
    }
  }

  if (target.abTestVariantList) {
    const {
      userId,
    } = relationshipData
    if (!userId) return false

    try {
      if (abTest(userId, target.abTestVariantList) !== true) {
        return false
      }
    } catch(e) {
      loggerBase().error({ userId, target }, 'A/B test error when generating personalized offer')
    }
  }

  if (target.countryList) {
    const {
      phoneCountry,
      countryWhitelist = [],
    } = userRelationshipData

    const userCountryList = [phoneCountry, ...countryWhitelist].filter(c => c)

    if (!target.countryList.some(country => userCountryList.includes(country))) {
      return false
    }
  }

  if (target.keywordList) {
    const keywordCheckResults = target.keywordList.map(keyword => {
      const keywordLower = keyword.toLowerCase()
      const { fullName = '', username = '' } = userRelationshipData          
      // Check if some connected account metadata contains keyword
      const accountContainsKeyword = connectedAccountList.some(account => {
        const { bio = '', name = '', mostRecentHashtagList = [] } = account
        return bio.toLowerCase().includes(keywordLower) ||
          name.toLowerCase().includes(keywordLower) ||
          mostRecentHashtagList.some(hashtag => (`#${hashtag}`).toLowerCase().includes(keywordLower))
        })
      // Also check if reach user metadata cotains the keyword
      return fullName.toLowerCase().includes(keywordLower) || username.toLowerCase().includes(keywordLower) || accountContainsKeyword
    })
    if (!keywordCheckResults.some(result => result)) return false
  }

  if (target.lastActivity) {
    const {
      latestSessionDayNumber,
    } = userRelationshipData
    if (!latestSessionDayNumber) return false

    const { periodStartTime, periodEndTime } = target.lastActivity
    if (!periodStartTime || !periodEndTime) return false

    const periodStartDayNumber = statDayNumber(periodStartTime)
    const periodEndDayNumber = statDayNumber(periodEndTime)

    if (latestSessionDayNumber < periodStartDayNumber || latestSessionDayNumber > periodEndDayNumber) return false
  }

  const numberBasedTargetOptionList = [
    { targetKey: 'medianViewCount', userRelationshipDataKey: 'log10MedianViewCount', userRelationshipDataIsLog10: true },
    { targetKey: 'followerCount', userRelationshipDataKey: 'log10FollowerCount', userRelationshipDataIsLog10: true },
    { targetKey: 'medianEngagementRate', userRelationshipDataKey: 'log10MedianEngagementRate', userRelationshipDataIsLog10: true },
    { targetKey: 'medianEngagementQuality', userRelationshipDataKey: 'log10MedianEngagementQuality', userRelationshipDataIsLog10: true },
    { targetKey: 'medianDashboardScore', userRelationshipDataKey: 'log10MedianDashboardScore', userRelationshipDataIsLog10: true },
  ]

  const someNumberBasedTargetFailed = numberBasedTargetOptionList
    .map(({ targetKey, userRelationshipDataKey, userRelationshipDataIsLog10 }) => {
      const { atLeast, atMost } = target[targetKey] || {}
      const rawUserValue = selectedLog10Metrics[userRelationshipDataKey]
      const userValue = rawUserValue === undefined ? undefined : (userRelationshipDataIsLog10 ? Math.pow(10, rawUserValue) : rawUserValue)

      if (atLeast) {
        if (!userValue) return false
        if (!(userValue >= atLeast)) return false
      }

      if (atMost) {
        if (!userValue) return false
        if (!(userValue <= atMost)) return false
      }

      return true
    })
    .some(result => !result)
  if (someNumberBasedTargetFailed) return false

  return true
}

exports.getRelevantOfferDefinition = function(relationshipData) {
  const {
    brandCampaignRelationshipData = {},
  } = relationshipData

  const {
    offer: defaultOffer,
    priorityOfferList = [],
  } = brandCampaignRelationshipData

  return priorityOfferList.find(({ disabled, target = {} }) => !disabled && exports.getRelationshipMatchesTarget(relationshipData, target)) || defaultOffer || {}
}

exports.getAnalyticsDataForBrandCampaign = (brandCampaignId, brandCampaign = {}) => {
  const {
    brandId,
    campaignType,
    economicType,
    earningsEnabled,
    userSwipesEnabled,
    userSwipesEnabledFirstDate,
    firstApplicationApprovalDate,
    firstSubmissionApprovalDate,
    campaignCompletionDate,
    linearData: { linearProjectLead = {} } = {},
  } = brandCampaign

  const data = {
    brandCampaignId,
    brandId,
    campaignType,
    brandCampaignEconomicType: economicType,
    brandCampaignEarningsEnabled: earningsEnabled,
    brandCampaignUserSwipesEnabled: userSwipesEnabled,
    brandCampaignObjectivesCompleted: Boolean(campaignCompletionDate),
    linearProjectLead: filterObjectKeys(linearProjectLead, ['name', 'id']),
  }

  const userSwipesEnabledFirstDateDate = date(userSwipesEnabledFirstDate)
  if (userSwipesEnabledFirstDateDate) data.brandCampaignDaysSinceUserSwipesEnabled = Number(((Date.now() - userSwipesEnabledFirstDateDate.getTime()) / ONE_DAY).toFixed(2))
  const firstApplicationApprovalDateDate = date(firstApplicationApprovalDate)
  if (firstApplicationApprovalDateDate) data.brandCampaignDaysSinceFirstApplicationApproval = Number(((Date.now() - firstApplicationApprovalDateDate.getTime()) / ONE_DAY).toFixed(2))
  const firstSubmissionApprovalDateDate = date(firstSubmissionApprovalDate)
  if (firstSubmissionApprovalDateDate) data.brandCampaignDaysSinceFirstSubmissionApproval = Number(((Date.now() - firstSubmissionApprovalDateDate.getTime()) / ONE_DAY).toFixed(2))

  return data
}

exports.getAnalyticsDataForBrandCampaignUserRelationship = (brandCampaignUserRelationshipId, brandCampaignUserRelationship = {}) => {
  const {
    brandCampaignId,
    brandCampaignRelationshipData = {},
    userRelationshipData = {},
    offerAccepted,
    offerAcceptedDate,
    manualMatchSource,
    brandApproved,
    brandApprovalDate,
    brandApprovedInstantly,
    personalizedOffer,
    personalizedOfferWhenAccepted,
    customLog10TargetViewCount,
    customEarnings,
    stats = {},
    submission,
    draft,
    submissionQualityScore,
    brandCampaignApplicationCountWhenApplied,
    brandCampaignPendingApplicationCountWhenApplied,
  } = brandCampaignUserRelationship

  const { log10MetricsByPlatform = {} } = userRelationshipData
  const submissionType = exports.getSubmissionTypeFromRelationship(brandCampaignUserRelationship)
  const externalPlatform = exports.getExternalPlatformBySubmittableType(submissionType)

  const selectedLog10Metrics = log10MetricsByPlatform[externalPlatform] || {}
  const { log10MedianViewCount = 0 } = selectedLog10Metrics

  const { minAverageViewCountThreshold = 0, maxAverageViewCountThreshold = Number.MAX_SAFE_INTEGER } = Object.values(brandCampaignRelationshipData.offer || {})
    .find(value => value.minAverageViewCountThreshold !== undefined) || {}
  const defaultLog10ViewCount = Math.min(Math.max(log10MedianViewCount, Math.log10(minAverageViewCountThreshold)), Math.log10(maxAverageViewCountThreshold))

  const filteredStats = {}
  Object.keys(stats).forEach((key) => {
    const value = stats[key]
    if (typeof value === 'number') filteredStats[key] = value
  })

  const result = {
    ...exports.getAnalyticsDataForBrandCampaign(brandCampaignId, brandCampaignRelationshipData),
    relationshipId: brandCampaignUserRelationshipId,
    offerAccepted,
    brandApproved,
    brandApprovedInstantly,
    brandCampaignUserCohort: exports.getBrandCampaignUserCohort(brandCampaignUserRelationship),
    brandCampaignPersonalizedCustomEarnings: customEarnings,
    brandCampaignPersonalizedStats: filteredStats,
    brandCampaignSubmission: submission,
    brandCampaignDraft: draft,
    submissionQualityScore,
    brandCampaignApplicationCountWhenApplied,
    brandCampaignPendingApplicationCountWhenApplied,
  }

  const offerAcceptedTime = date(offerAcceptedDate)?.getTime()
  const brandApprovalTime = date(brandApprovalDate)?.getTime()
  if (offerAcceptedTime) result.hoursSinceOfferAccepted = (Date.now() - offerAcceptedTime) / ONE_HOUR
  if (brandApprovalTime) result.hoursSinceBrandApproval = (Date.now() - brandApprovalTime) / ONE_HOUR
  if (offerAcceptedTime && brandApprovalTime) result.hoursBetweenOfferAcceptedAndBrandApproval = (brandApprovalTime - offerAcceptedTime) / ONE_HOUR

  if (manualMatchSource) Object.assign(result, { manualMatchSource })

  if (customLog10TargetViewCount !== undefined) result.editedViewCountMultiplier = Math.pow(10, customLog10TargetViewCount - defaultLog10ViewCount).toFixed(1)

  const relevantOfferDefinition = exports.getRelevantOfferDefinition(brandCampaignUserRelationship)
  const relevantPersonalizedOffer = (offerAccepted && personalizedOfferWhenAccepted) || personalizedOffer || {}
  if ((relevantPersonalizedOffer.earningsForViewCountThresholdList || []).length) {
    const [{ viewCountThreshold: minViewCountThreshold }] = relevantPersonalizedOffer.earningsForViewCountThresholdList.sort((a, b) => (a.viewCountThreshold || 0) - (b.viewCountThreshold || 0))
    relevantPersonalizedOffer.minViewCountThreshold = minViewCountThreshold
  }

  result.brandCampaignPersonalizedOffer = filterObjectKeys(relevantPersonalizedOffer, ['maxEarnings', 'maxViewCountThreshold', 'minViewCountThreshold', 'submissionEarningsWindowDuration'])

  if (relevantPersonalizedOffer.maxEarnings !== undefined && relevantPersonalizedOffer.maxViewCountThreshold !== undefined) {
    result.brandCampaignPersonalizedOffer.cpm = relevantPersonalizedOffer.maxEarnings / relevantPersonalizedOffer.maxViewCountThreshold * 1000
  }

  if (stats.viewCount !== undefined && relevantPersonalizedOffer.maxViewCountThreshold !== undefined) {
    result.viewCountOverMaxOffer = stats.viewCount / relevantPersonalizedOffer.maxViewCountThreshold
  }

  if (stats.viewCount !== undefined && relevantPersonalizedOffer.minViewCountThreshold !== undefined) {
    result.viewCountOverMinOffer = stats.viewCount / relevantPersonalizedOffer.minViewCountThreshold
  }

  if (relevantOfferDefinition.custom) {
    result.brandCampaignOfferType = 'custom'
    if (typeof relevantOfferDefinition.custom === 'object') {
      result.brandCampaignOfferDefaultEarnings = relevantOfferDefinition.custom.defaultEarnings
    }
  } else if (relevantOfferDefinition.smartSteppedCpm) {
    result.brandCampaignOfferType = 'smart-stepped-cpm'
  } else if (relevantOfferDefinition.steppedEarningsPerThousandViews) {
    result.brandCampaignOfferType = 'tiered-constant-cpm'
  } else if (relevantOfferDefinition.steppedEarningsPerThousandViewsWithPolynomialCpm) {
    result.brandCampaignOfferType = 'tiered-polynomial-cpm'
  }

  return result
}

exports.getDiscoverOrderIndex = (brandCampaignData) => {
  const {
    userSwipesEnabled,
    discoverManualOrderIndex,
    userSwipesEnabledDate,
    economicType,
  } = brandCampaignData

  if (!userSwipesEnabled) return undefined
  else if (discoverManualOrderIndex) return discoverManualOrderIndex
  else {
    const swipesEnabledDate = date(userSwipesEnabledDate)
    const swipesEnabledTime = swipesEnabledDate ? swipesEnabledDate.getTime() : 0
    let result = Math.max(0, (swipesEnabledTime - (ONE_YEARISH * 50)) / (ONE_YEARISH * 10))
    if (economicType === 'cogs') result += 1
    return result
  }
}

exports.generateTemplateReplacementObject = function(originEntity, recipientEntity, relationshipData = {}) {
  const result = {}

  const submissionType = exports.getSubmissionTypeFromRelationship(relationshipData)
  const externalPlatform = exports.getExternalPlatformBySubmittableType(submissionType)

  const originEntityId = originEntity?.id
  const recipientEntityId = recipientEntity.id
  const originEntityType = originEntity?.type
  const recipientEntityType = recipientEntity.type
  const originEntityData = originEntity?.data || {}
  const recipientEntityData = recipientEntity.data || {}

  if (originEntityId) result.ORIGIN_ID = originEntityId
  if (originEntityData.fullName) result.ORIGIN_FULL_NAME = originEntityData.fullName
  if (originEntityData.username) result.ORIGIN_USERNAME = originEntityData.username
  if (originEntityData.submissionRequirements) result.ORIGIN_REQUIREMENTS = originEntityData.submissionRequirements
  if (originEntityData.tiktokSoundUrl) result.ORIGIN_SONG_URL = originEntityData.tiktokSoundUrl
  if (originEntityData.tiktokSoundName) result.ORIGIN_SONG_NAME = originEntityData.tiktokSoundName
  if (originEntityData.soundUrl) result.ORIGIN_SONG_URL = originEntityData.soundUrl
  if (originEntityData.soundName) result.ORIGIN_SONG_NAME = originEntityData.soundName

  if (recipientEntityId) result.RECIPIENT_ID = recipientEntityId
  if (recipientEntityData.fullName) result.RECIPIENT_FULL_NAME = recipientEntityData.fullName
  if (recipientEntityData.username) result.RECIPIENT_USERNAME = recipientEntityData.username
  if (recipientEntityData.mostRelevantTiktokUsername) result.RECIPIENT_TIKTOK_USERNAME = recipientEntityData.mostRelevantTiktokUsername
  if (recipientEntityData.mostRelevantInstagramUsername) result.RECIPIENT_INSTAGRAM_USERNAME = recipientEntityData.mostRelevantInstagramUsername

  if (originEntityType === 'brandCampaign' && recipientEntityType === 'user') {
    const { metricsByPlatform = {}, log10MetricsByPlatform = {} } = recipientEntityData || {}
    const selectedMaxConnectedMetricsByPlatform = metricsByPlatform[externalPlatform] || {}
    const selectedLog10MetricsByPlatform = log10MetricsByPlatform[externalPlatform] || {}

    if (selectedMaxConnectedMetricsByPlatform.maxConnectedAverageViewCount !== undefined) result.RECIPIENT_AVG_VIEW_COUNT = selectedMaxConnectedMetricsByPlatform.maxConnectedAverageViewCount
    else if (selectedLog10MetricsByPlatform.log10AverageViewCount !== undefined) result.RECIPIENT_AVG_VIEW_COUNT = Math.pow(10, selectedLog10MetricsByPlatform.log10AverageViewCount)

    if (selectedMaxConnectedMetricsByPlatform.maxConnectedMedianViewCount !== undefined) result.RECIPIENT_MEDIAN_VIEW_COUNT = selectedMaxConnectedMetricsByPlatform.maxConnectedMedianViewCount
    else if (selectedLog10MetricsByPlatform.log10MedianViewCount !== undefined) result.RECIPIENT_MEDIAN_VIEW_COUNT = Math.pow(10, selectedLog10MetricsByPlatform.log10MedianViewCount)

    const offerPreview = (relationshipData.offerAccepted && relationshipData.personalizedOfferPreviewWhenAccepted) || relationshipData.personalizedOfferPreview
    if (offerPreview) result.PERSONALIZED_OFFER_PREVIEW = offerPreview

    const { maxEarnings, earningsForViewCountThresholdList, defaultEarnings } = (relationshipData.offerAccepted && relationshipData.personalizedOfferWhenAccepted) || relationshipData.personalizedOffer || {}
    if (maxEarnings) result.PERSONALIZED_OFFER_MAX_EARNINGS = `$${exports.formatOfferEarningsNicelyAsString(maxEarnings)}`

    if (defaultEarnings) {
      // Guaranteed earnings from a custom offer structure's default earnings
      if (defaultEarnings > 0) result.PERSONALIZED_OFFER_GUARANTEED_EARNINGS = `$${exports.formatOfferEarningsNicelyAsString(defaultEarnings)}`
    } else if (earningsForViewCountThresholdList) {
      // Guaranteed earnings mean the threshold where the user must hit 1 view
      const maybeGuaranteedTier = earningsForViewCountThresholdList.find(({ viewCountThreshold }) => viewCountThreshold <= 1)
      if (maybeGuaranteedTier) {
        const { earnings: guaranteedEarnings } = maybeGuaranteedTier
        if (guaranteedEarnings) {
          if (guaranteedEarnings > 0) result.PERSONALIZED_OFFER_GUARANTEED_EARNINGS = `$${exports.formatOfferEarningsNicelyAsString(guaranteedEarnings)}`
        }
      }
    }

    const { lockedEarnings } = relationshipData.stats || {}
    if (lockedEarnings) result.LOCKED_EARNINGS = `$${formatNumber(lockedEarnings)}`
  }

  return result
}

exports.getRelevantStatsList = function(brandCampaignOrLog, onPlatform /* true = on platform, false = off platform, undefined = both */, includeOfferedProjections, includePendingSubmissionProjections) {
  const { cohortStats = {}, offPlatformStats = {} } = brandCampaignOrLog

  const onPlatformCohortList = []
  if (onPlatform !== false) {
    onPlatformCohortList.push('submitted-approved', 'submitted-approved-certified')
    onPlatformCohortList.push('applied-drafting', 'applied-staged', 'applied-approved')
    if (includeOfferedProjections) onPlatformCohortList.push('offered')
    if (includePendingSubmissionProjections) onPlatformCohortList.push('applied-draft', 'submitted-pending', 'submitted-fraud-alert')
  }

  const offPlatformCohortList = []
  if (onPlatform !== true) {
    offPlatformCohortList.push(...Object.keys(offPlatformStats))
  }

  return [
    ...onPlatformCohortList.map(cohort => cohortStats[cohort] || {}),
    ...offPlatformCohortList.map(cohort => offPlatformStats[cohort] || {}),
  ]
}


exports.getBrandCampaignInternalDurationPaused = (brandCampaign) => {
  const { internalDurationPauseList = [] } = brandCampaign
  return internalDurationPauseList
    .some(({ startDate = Date.now(), endDate }) => (Date.now() >= date(startDate).getTime()) && (!endDate || Date.now() < date(endDate).getTime()))
}


exports.getBrandCampaignDurationStats = (brandCampaign, atDateOrTime = Date.now()) => {
  const atTime = date(atDateOrTime).getTime()

  const {
    internalTargetDurationDays,
    internalTargetFirstResultDays = 3,
    internalDurationPauseList = [],
  } = brandCampaign

  const internalTargetLaunchTime = date(brandCampaign.internalTargetLaunchDate)?.getTime()
  const campaignCompletionTime = date(brandCampaign.campaignCompletionDate)?.getTime()

  let startTime = internalTargetLaunchTime

  let currentDurationDays
  if (internalTargetLaunchTime) {
    if (campaignCompletionTime) {
      currentDurationDays = (campaignCompletionTime - internalTargetLaunchTime) / ONE_DAY
    } else {
      currentDurationDays = (atTime - internalTargetLaunchTime) / ONE_DAY
    }
  }

  internalDurationPauseList.forEach(({ startDate, endDate }) => {
    const startTime = date(startDate)?.getTime()
    const endTime = date(endDate)?.getTime() || atTime

    if (startTime) {
      currentDurationDays -= Math.max(0, Math.min(endTime, atTime) - startTime) / ONE_DAY
    }
  })

  let delayedTargetDurationDays
  let delayedDurationDays
  let currentDurationProgress
  let delayedDurationProgress
  if (internalTargetDurationDays) {
    delayedTargetDurationDays = internalTargetDurationDays - internalTargetFirstResultDays

    if (currentDurationDays !== undefined) {
      delayedDurationDays = currentDurationDays - internalTargetFirstResultDays
      currentDurationProgress = currentDurationDays / internalTargetDurationDays
      delayedDurationProgress = delayedDurationDays / Math.max(1, delayedTargetDurationDays)
    }
  }

  return {
    internalTargetLaunchTime,
    campaignCompletionTime,
    internalTargetDurationDays,
    delayedTargetDurationDays,
    currentDurationDays,
    delayedDurationDays,
    currentDurationProgress,
    delayedDurationProgress,
  }
}


exports.getBrandCampaignObjectivesProgress = (brandCampaign) => {
  const {
    internalTargetLaunchTime,
    campaignCompletionTime,
    internalTargetDurationDays,
    delayedTargetDurationDays,
    currentDurationDays,
    delayedDurationDays,
    currentDurationProgress,
    delayedDurationProgress,
  } = exports.getBrandCampaignDurationStats(brandCampaign)


  const communications = {}

  const latestBrandCommunicationTime = date(brandCampaign.latestBrandCommunicationDate)?.getTime()
  if (latestBrandCommunicationTime) {
    communications.daysSinceLatest = (Date.now() - latestBrandCommunicationTime) / ONE_DAY
    communications.latestIsAfterCampaignCompletion = Boolean(campaignCompletionTime) && latestBrandCommunicationTime >= campaignCompletionTime
  }
  const brandCommunicationDashboardFirstSendTime = date(brandCampaign.brandCommunicationDashboardFirstSendDate)?.getTime()
  if (brandCommunicationDashboardFirstSendTime) {
    communications.daysSinceDashboardSent = (Date.now() - brandCommunicationDashboardFirstSendTime) / ONE_DAY
    if (internalTargetLaunchTime) {
      communications.daysFromLaunchToDashboardSent = Math.max(0, brandCommunicationDashboardFirstSendTime - internalTargetLaunchTime) / ONE_DAY
    }
  }
  const latestBrandIncomingCommunicationTime = date(brandCampaign.latestBrandIncomingCommunicationDate)?.getTime()
  if (latestBrandIncomingCommunicationTime) {
    communications.daysSinceLatestIncoming = (Date.now() - latestBrandIncomingCommunicationTime) / ONE_DAY
    communications.incomingIsMoreRecentThanOutgoing = latestBrandIncomingCommunicationTime >= latestBrandCommunicationTime
  }
  const { latestBrandIncomingCommunicationSentiment } = brandCampaign
  if (latestBrandIncomingCommunicationSentiment !== undefined) {
    communications.latestIncomingSentiment = latestBrandIncomingCommunicationSentiment
  }

  const result = {
    duration: {
      value: currentDurationDays,
      target: internalTargetDurationDays,
      progress: currentDurationProgress,
      delayedValue: delayedDurationDays,
      delayedTarget: delayedTargetDurationDays,
      delayedProgress: delayedDurationProgress,
    },
    communications,
  }

  const campaignCompletionAmount = brandCampaign.campaignCompletionDate ? 1 : Math.max(0, Math.min(1, brandCampaign.campaignCompletionAmount || 0))

  const statsList = exports.getRelevantStatsList(brandCampaign)

  const objectiveList = [
    {
      objectiveKey: 'campaignCompletionAmount',
      objectiveValue: 1,
      progressValue: campaignCompletionAmount,
    },
    {
      objectiveKey: 'internalTargetBudget',
      progressValue: statsList.reduce((prevResult, stats) => prevResult + (stats.lockedEarnings || 0), 0),
    },
    {
      objectiveKey: 'internalTargetCreateCount',
      progressValue: statsList.reduce((prevResult, stats) => prevResult + (stats.createCount || 0), 0),
    },
    {
      objectiveKey: 'internalTargetViewCount',
      progressValue: statsList.reduce((prevResult, stats) => prevResult + (stats.viewCount || 0), 0),
    },
    {
      objectiveKey: 'internalTargetEngagementRate',
      progressValue: (() => {
        const viewCount = statsList.reduce((prevResult, { viewCount = 0 } = {}) => prevResult + viewCount, 0)
        const engagementCount = statsList.reduce((prevResult, { likeCount = 0, commentCount = 0, shareCount = 0, saveCount = 0 } = {}) => prevResult + likeCount + commentCount + shareCount + saveCount, 0)
        if (!viewCount) return undefined
        return engagementCount / viewCount
      })(),
    },
  ]

  objectiveList.forEach(({ objectiveKey, objectiveValue, progressValue: value }) => {
    const target = objectiveValue !== undefined ? objectiveValue : brandCampaign[objectiveKey]
    let progress
    let leadOfDurationProgress
    let leadOfDurationDelayedProgress
    let fractionOfDurationProgress
    let fractionOfDurationDelayedProgress
    if (target && value !== undefined) {
      progress = value / target
      if (currentDurationProgress !== undefined) {
        leadOfDurationProgress = progress - currentDurationProgress
        leadOfDurationDelayedProgress = progress - delayedDurationProgress
      }
      if (currentDurationProgress > 0) {
        fractionOfDurationProgress = progress / currentDurationProgress
      }
      if (delayedDurationProgress > 0) {
        fractionOfDurationDelayedProgress = progress / delayedDurationProgress
      }
    }
    result[objectiveKey] = {
      value,
      target,
      progress,
      leadOfDurationProgress,
      leadOfDurationDelayedProgress,
      fractionOfDurationProgress,
      fractionOfDurationDelayedProgress,
    }
  })

  return result
}


// Aggregates how many users applied to a campaign
exports.getApplicationCount = function(brandCampaignData) {
  const { cohortStats = {} } = brandCampaignData || {}
  return Object.keys(cohortStats).reduce((appCount, cohortKey) => {
    if (!cohortKey.startsWith('applied') && !cohortKey.startsWith('submitted')) return appCount

    const selectedCohort = cohortStats[cohortKey] || {}
    return appCount + selectedCohort.userCount || 0
  }, 0)
}

// Returns number of pending applications
exports.getPendingApplicationCount = function(brandCampaignData) {
  const { cohortStats = {} } = brandCampaignData || {}
  const pendingApplicationCohort = cohortStats['applied-pending'] || {}
  return pendingApplicationCohort.userCount || 0
}
